import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  NOMBRE_DE_RED,
  ORIGEN_DEL_EMBED,
  medidaDeEmbed,
  urlDeEmbedIframe,
  type EmbedProvider,
} from '@ahora-patagonia/contracts';
import { neutral } from '@ahora-patagonia/design-tokens';

export interface EmbedDeRedProps {
  provider: EmbedProvider;
  postId: string | null;
  url: string;
  caption: string | null | undefined;
}

/** Un alto que no venga de este rango no es una medida, es un error o un intento de estirar la página. */
const ALTO_MINIMO = 100;
const ALTO_MAXIMO = 4000;

/**
 * Publicación de una red embebida en el cuerpo.
 *
 * **Carga de entrada**, a diferencia del video: se decidió que el lector vea la publicación sin
 * tener que tocar nada. El costo está asumido y es real —el embed de Instagram anda por 1 MB y
 * planta cookies de terceros—, así que al menos va con `lazy` y con la caja reservada.
 *
 * El iframe sale en el HTML del SSR y no hay estado inicial que pueda diferir entre el server y el
 * cliente: la medida arranca en la proporción de la red en los dos lados.
 *
 * **El alto es el punto flojo.** Ninguno de estos iframes se ajusta solo. Instagram y X mandan su
 * alto real por `postMessage` y ahí la caja crece; Facebook y TikTok no lo mandan, así que se
 * quedan con la proporción y, si la publicación es más larga, se scrollea adentro.
 */
export function EmbedDeRed({ provider, postId, url, caption }: EmbedDeRedProps) {
  const [alto, setAlto] = useState<number | null>(null);
  const medida = medidaDeEmbed(provider);
  const red = NOMBRE_DE_RED[provider];

  useEffect(() => {
    const origenEsperado = ORIGEN_DEL_EMBED[provider];
    const alRecibir = (evento: MessageEvent) => {
      // Se verifica el origen **antes** de creerle a la medida: sin esto, cualquier iframe de la
      // página (una publicidad, por ejemplo) podría estirar el bloque a lo que quisiera.
      if (evento.origin !== origenEsperado) return;
      const medido = altoDelMensaje(evento.data);
      if (medido !== null) setAlto(medido);
    };
    window.addEventListener('message', alRecibir);
    return () => window.removeEventListener('message', alRecibir);
  }, [provider]);

  return (
    <Box component="figure" sx={{ m: 0, my: 3, maxWidth: medida.anchoMaximo, mx: 'auto' }}>
      <Box
        component="iframe"
        src={urlDeEmbedIframe({ provider, postId, url })}
        title={`Publicación de ${red}`}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="encrypted-media; picture-in-picture"
        sx={{
          display: 'block',
          width: '100%',
          border: 0,
          bgcolor: neutral[200],
          // Hasta que llegue la medida real, la proporción de la red reserva la caja: sin esto el
          // texto de la nota salta cuando entra cada publicación.
          ...(alto === null ? { aspectRatio: medida.proporcion } : { height: alto }),
        }}
      />
      <Typography component="figcaption" variant="epigrafe" sx={{ color: neutral[700], mt: 0.5 }}>
        {caption ? `${caption} · ` : ''}
        {/*
          El enlace es lo único que le queda a un buscador, a un lector de pantalla y a quien tenga
          los iframes bloqueados. Si mañana borran la publicación, esto sigue diciendo qué era.
        */}
        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'inherit' }}
        >
          Ver la publicación en {red}
        </Box>
      </Typography>
    </Box>
  );
}

/**
 * El alto que manda el embed, en los dos formatos que existen: el `MEASURE` de Instagram y el
 * `twttr.private.resize` de X. Cualquier otra cosa se ignora — no hay razón para adivinar.
 */
function altoDelMensaje(data: unknown): number | null {
  const mensaje = typeof data === 'string' ? parsearJson(data) : data;
  if (!mensaje || typeof mensaje !== 'object') return null;

  const m = mensaje as {
    type?: string;
    details?: { height?: unknown };
    'twttr.embed'?: { params?: { height?: unknown }[] };
  };

  const crudo =
    m.type === 'MEASURE' ? m.details?.height : m['twttr.embed']?.params?.[0]?.height;

  if (typeof crudo !== 'number' || !Number.isFinite(crudo)) return null;
  const alto = Math.round(crudo);
  return alto >= ALTO_MINIMO && alto <= ALTO_MAXIMO ? alto : null;
}

function parsearJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}
